import * as React from "react"

import useStatefulField from "shared/hooks/useStatefulField"
import { Dimension, DimensionType, DimensionUnit, NutrientWeight, Volume, Weight } from "shared/hubrise/models"
import { normaliseDimension } from "shared/hubrise/utils/Sku"

import { StyledInput } from "./Styles"

interface DimensionInputProps<D extends Dimension> {
  className?: string
  initialValue: D | null
  placeholder: string
  filter: RegExp
  dimensionType: DimensionType<D>
  defaultUnit: DimensionUnit<D>
  onChange: (value: D | null) => void
}

const DimensionInput = <D extends Dimension>({
  className,
  initialValue,
  placeholder,
  filter,
  dimensionType,
  defaultUnit,
  onChange,
}: DimensionInputProps<D>): JSX.Element => {
  const { value, handleChange, handleBlur } = useStatefulField<string>({
    initialValue: initialValue ?? "",
    onChange: (val) => onChange(normaliseDimension<D>(val, dimensionType, defaultUnit)),
  })

  const handleInputBlur = (): void => {
    const normalised = normaliseDimension<D>(value, dimensionType, defaultUnit)
    handleChange(normalised ?? "")
    handleBlur()
  }

  return (
    <StyledInput
      className={className}
      value={value ?? ""}
      placeholder={placeholder}
      filter={filter}
      onChange={handleChange}
      onBlur={handleInputBlur}
    />
  )
}

const WEIGHT_FILTER = /^\s*(\d+\.?\d*)?\s*(m|mg|g|k|kg)?\s*$/
const VOLUME_FILTER = /^\s*(\d+\.?\d*)?\s*(m|ml|c|cl|l)?\s*$/
const NUTRIENT_WEIGHT_FILTER = /^\s*(\d+\.?\d*)?\s*(u|ug|µ|µg|m|mg|g)?\s*$/

type ExportedProps<D extends Dimension> = Omit<DimensionInputProps<D>, "filter" | "dimensionType">

const WeightInput = (props: ExportedProps<Weight>): JSX.Element => (
  <DimensionInput {...props} filter={WEIGHT_FILTER} dimensionType="weight" />
)

const VolumeInput = (props: ExportedProps<Volume>): JSX.Element => (
  <DimensionInput {...props} filter={VOLUME_FILTER} dimensionType="volume" />
)

const NutrientWeightInput = (props: ExportedProps<NutrientWeight>): JSX.Element => (
  <DimensionInput {...props} filter={NUTRIENT_WEIGHT_FILTER} dimensionType="nutrient_weight" />
)

export { WeightInput, VolumeInput, NutrientWeightInput }
